/** Fetch wrapper for the admin API: bearer token, problem+json errors with a stable code, 204 handling, downloads. */
const TOKEN_KEY = 'compass.admin.token'

export class ApiError extends Error {
  status: number
  code: string
  /** For VALIDATION_FAILED: a plain-language message per field. */
  fields: Record<string, string>
  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message)
    this.status = status
    this.code = code
    this.fields = fields
  }
}

let token: string | null = read()
function read(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export const getToken = () => token
export function setToken(value: string | null): void {
  token = value
  try {
    if (value) sessionStorage.setItem(TOKEN_KEY, value)
    else sessionStorage.removeItem(TOKEN_KEY)
  } catch {
    // Kept in memory for this tab only.
  }
}

export const UNAUTHORIZED_EVENT = 'compass-admin:unauthorized'

interface Options {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  form?: FormData
  /** Use an interim token (MFA step) instead of the stored session token. */
  bearer?: string
  query?: Record<string, string | number | boolean | null | undefined>
}

export function withQuery(path: string, query?: Options['query']): string {
  if (!query) return path
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) if (v !== null && v !== undefined && v !== '') p.set(k, String(v))
  const s = p.toString()
  return s ? `${path}?${s}` : path
}

async function send(path: string, o: Options): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const bearer = o.bearer ?? token
  if (bearer) headers.Authorization = `Bearer ${bearer}`
  if (o.body !== undefined) headers['Content-Type'] = 'application/json'
  let res: Response
  try {
    res = await fetch(withQuery(path, o.query), { method: o.method ?? 'GET', headers, body: o.form ?? (o.body === undefined ? undefined : JSON.stringify(o.body)) })
  } catch {
    throw new ApiError(0, 'NETWORK', 'Cannot reach the server. Check your connection.')
  }
  if (res.status === 401 && token && !o.bearer) {
    setToken(null)
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
  }
  if (!res.ok) {
    const text = await res.text()
    let p: { code?: string; detail?: string; title?: string; fields?: Record<string, string> } = {}
    try {
      p = text ? JSON.parse(text) : {}
    } catch {
      // Not JSON.
    }
    throw new ApiError(res.status, p.code ?? `HTTP_${res.status}`, p.detail ?? p.title ?? 'Something went wrong', p.fields && !Array.isArray(p.fields) ? p.fields : {})
  }
  return res
}

export async function api<T>(path: string, o: Options = {}): Promise<T> {
  const res = await send(path, o)
  if (res.status === 204) return undefined as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/** Downloads a file with the session token and saves it through the browser. */
export async function download(path: string, filename: string, query?: Options['query']): Promise<void> {
  const res = await send(path, { query })
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
